import { detectPackage, formatSurface, sortGames } from "@/lib/utils";

function openPrint(html, filename) {
  const w = window.open("", "_blank");
  if (!w) {
    alert("Autorise les pop-ups pour générer le PDF.");
    return;
  }
  w.document.write(html);
  w.document.close();
  w.document.title = filename;
  setTimeout(() => w.print(), 500);
}

const baseCss = `
  body { font-family: Arial, Helvetica, sans-serif; color: #0A0A0A; padding: 32px; background: #fff; }
  h1 { font-family: Arial, Helvetica, sans-serif; font-weight: 700; font-size: 36px; margin: 0 0 4px; letter-spacing: -0.02em; }
  .label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.15em; color: #525252; }
  table { width: 100%; border-collapse: collapse; margin-top: 16px; font-size: 11px; }
  th { text-align: left; padding: 8px 6px; border-bottom: 2px solid #000; text-transform: uppercase; font-size: 9px; letter-spacing: 0.12em; color: #525252; }
  td { padding: 8px 6px; border-bottom: 1px solid #e5e5e5; }
  .total-row td { font-weight: 700; border-top: 2px solid #000; padding-top: 12px; }
  .break-row td { background: #171717; color: #fff; }
  .meta { color: #525252; font-size: 12px; margin-bottom: 24px; }
  .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid #000; font-size: 10px; color: #525252; }
  .right { text-align: right; }
`;

export function generateClientPDF(event, officials, grades, settings, extras = []) {
  const packages = settings.packages || {};
  const games = sortGames(event.games || []);
  const counts = { A: 0, B: 0, C: 0, D: 0 };

  // 1) Compter parties par forfait
  games.forEach((g) => {
    if (g.is_break) return;
    const pkg = detectPackage(g);
    if (pkg) counts[pkg]++;
  });

  // 2) Lignes par forfait (uniquement ceux > 0)
  let subtotal = 0;
  const pkgRows = ["A", "B", "C", "D"]
    .filter((k) => counts[k] > 0)
    .map((k) => {
      const pkg = packages[k] || {};
      const rate = Number(pkg.rate || 0);
      const lineTotal = counts[k] * rate;
      subtotal += lineTotal;
      return `<tr>
        <td>${counts[k]} partie${counts[k] > 1 ? "s" : ""}</td>
        <td>Forfait ${k}${pkg.label ? ` — ${pkg.label}` : ""}</td>
        <td class="right tabular">${rate.toFixed(2)} $</td>
        <td class="right tabular">${lineTotal.toFixed(2)} $</td>
      </tr>`;
    })
    .join("");

  // 3) Total gaz (un seul item)
  const gasTotal = (extras || [])
    .filter((x) => x.kind === "extra")
    .reduce((n, x) => n + Number(x.amount || 0), 0);

  const grandTotal = subtotal + gasTotal;

  // 4) Dates de l'événement (multi-jours = compact)
  const eventDates = [...new Set(games.map((g) => g.date).filter(Boolean))].sort();
  let datesLabel = "";
  if (eventDates.length > 0) datesLabel = eventDates.join(" · ");
  else if (event.date) datesLabel = event.date;

  // 5) Bloc client
  const clientBlock = `
    <div class="client-card">
      <div class="label">Facturé à</div>
      <div class="client-name">${event.client || "—"}</div>
      <div class="client-meta">${event.name || ""}</div>
      ${datesLabel ? `<div class="client-meta">${datesLabel}</div>` : ""}
      ${event.location ? `<div class="client-meta">${event.location}</div>` : ""}
    </div>`;

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Facture — ${event.name}</title>
    <style>${baseCss}
      .header-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; gap: 24px; }
      .biz-block { text-align: right; font-size: 11px; color: #525252; line-height: 1.5; }
      .biz-name { color: #0A0A0A; font-weight: 700; font-size: 13px; margin-bottom: 4px; }
      .client-card { border: 2px solid #0A0A0A; padding: 18px 20px; margin-bottom: 28px; max-width: 60%; }
      .client-name { font-size: 22px; font-weight: 700; margin: 6px 0 8px; letter-spacing: -0.01em; }
      .client-meta { color: #525252; font-size: 12px; margin-top: 2px; }
      .invoice-table { margin-top: 8px; }
      .invoice-table th, .invoice-table td { font-size: 12px; padding: 12px 8px; }
      .subtotal-row td { padding-top: 16px; border-top: 1px solid #0A0A0A; font-weight: 600; }
      .gas-row td { color: #525252; }
      .grand-total td { border-top: 2px solid #0A0A0A; border-bottom: 2px solid #0A0A0A; font-size: 18px; padding: 16px 8px; font-weight: 700; letter-spacing: -0.01em; }
      .tabular { font-variant-numeric: tabular-nums; }
    </style></head><body>
    <div class="header-row">
      <div>
        <div class="label">Facture</div>
        <h1>${event.name || ""}</h1>
      </div>
      <div class="biz-block">
        <div class="biz-name">${settings.business_name || ""}</div>
        ${settings.business_phone ? `<div>${settings.business_phone}</div>` : ""}
      </div>
    </div>
    ${clientBlock}
    <table class="invoice-table">
      <thead><tr>
        <th>Quantité</th>
        <th>Description</th>
        <th class="right">Tarif</th>
        <th class="right">Sous-total</th>
      </tr></thead>
      <tbody>
        ${pkgRows || `<tr><td colspan="4" style="text-align:center;color:#525252;padding:24px">Aucune partie facturable.</td></tr>`}
        <tr class="subtotal-row">
          <td colspan="3" class="right">Sous-total</td>
          <td class="right tabular">${subtotal.toFixed(2)} $</td>
        </tr>
        ${gasTotal > 0 ? `<tr class="gas-row">
          <td colspan="3" class="right">Frais de déplacement (gaz)</td>
          <td class="right tabular">${gasTotal.toFixed(2)} $</td>
        </tr>` : ""}
        <tr class="grand-total">
          <td colspan="3" class="right">Montant total</td>
          <td class="right tabular">${grandTotal.toFixed(2)} $</td>
        </tr>
      </tbody>
    </table>
    <div class="footer">Émis le ${new Date().toLocaleDateString("fr-CA")} — ${settings.business_name || ""}</div>
    </body></html>`;
  openPrint(html, `${event.name.replace(/[^a-z0-9]/gi, "_")}_facture.pdf`);
}

export function generatePayrollPDF(event, payroll, total, settings) {
  const rows = payroll
    .map((p) => {
      const extrasInfo = (p.extras || []).map((x) => `+${Number(x.amount).toFixed(2)} $ ${x.label}`).join(", ");
      const dedInfo = (p.deductions || []).map((x) => `−${Number(x.amount).toFixed(2)} $ ${x.label}`).join(", ");
      const adj = [extrasInfo, dedInfo].filter(Boolean).join(" · ");
      const lines = p.gameLines || [];
      const refs = lines.filter((g) => g.role === "Arbitre").length;
      const scs = lines.filter((g) => g.role === "Marqueur").length;
      return `<tr>
      <td><b>${p.off.first_name} ${p.off.last_name}</b><br><span style="font-size:10px;color:#525252">${p.off.email || ""} ${p.off.phone ? "· " + p.off.phone : ""}</span>${adj ? `<br><span style="font-size:10px;color:#525252">${adj}</span>` : ""}</td>
      <td>${p.refGrade ? "Arb. " + p.refGrade.name : ""} ${p.scGrade ? "· Marq. " + p.scGrade.name : ""}</td>
      <td class="right">${refs}</td>
      <td class="right">${scs}</td>
      <td class="right"><b>${p.total.toFixed(2)} $</b></td>
    </tr>`;
    })
    .join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Paies — ${event.name}</title>
    <style>${baseCss}</style></head><body>
    <div class="label">${settings.business_name || ""} · Feuille de paie</div>
    <h1>${event.name}</h1>
    <div class="meta">${event.date || ""}${event.location ? " · " + event.location : ""}</div>
    <table><thead><tr><th>Officiel</th><th>Grade</th><th class="right">Arb.</th><th class="right">Marq.</th><th class="right">Montant</th></tr></thead>
    <tbody>${rows}<tr class="total-row"><td colspan="4">TOTAL À PAYER</td><td class="right">${total.toFixed(2)} $</td></tr></tbody></table>
    </body></html>`;
  openPrint(html, `${event.name.replace(/[^a-z0-9]/gi, "_")}_paies.pdf`);
}
